import {getLinkedDocument, getUrn, hasLinkedDocument, isIssueInSheet} from '../../utils/issues-helpers';
import i18n from '../../utils/i18n';
import {SHOW_TOAST_STATUS_LIST} from '../../constants/transitions';
import {ENV_CONFIGS} from '../../constants/envs';
import {viewTypes} from '../../constants/view-types';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import {NotificationLevels, NotificationTypes} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import {
  getAllAssignees,
  getChecklistCollection,
  getCurrentPreviewIssueAttributes,
  getCurrentPreviewIssueId,
  getCurrentSheets,
  getCurrentUserId,
  getCurrentViewType,
  getDocumentCollection,
  getFetchParams,
  getFieldIssueTypesAndSubTypesIdToTitleMap,
  getIssueAttributesById,
  getIssuesCollectionAttributes,
  getIssueTypeById,
  getLastQueryKey,
  getOxygenMappings,
  getPendingPushpinThumbnails,
  getProjectId,
  getQueryKey,
  getQueryKeyIsCurrent,
  isFieldManagementActive
} from '../selectors';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {attachmentOrigin} from '../../constants/attachments';
import {
  ATTACHMENT_DELETE_API_FULFILLED,
  ATTACHMENT_DELETE_API_REJECTED,
  ATTACHMENT_UPLOAD_FAILED,
  ATTACHMENTS_FETCH_API_FULFILLED,
  batchClearPushpinAction,
  batchCreatePushpinAction,
  clearPendingThumbnail,
  clearPushpinAction,
  clearUploadingAttachmentsListByOrigin,
  CLOSE_PUSHPIN_SELECTION,
  closeIframe,
  COMMENT_CREATE_API,
  COMMENT_CREATE_API_FULFILLED,
  COMMENT_CREATE_API_REJECTED,
  COMMENT_CREATE_START,
  CURRENT_USER_FETCH_API,
  CURRENT_USER_FETCH_START,
  fetchAttachmentThumbnailsAction,
  fetchDocumentDetailsIfMissing,
  fetchDocumentsWithIssues,
  fetchIssuesAction,
  FIELD_ISSUES_ACTIVITY_FETCH_ATTACHMENTS,
  ISSUE_CREATE_API,
  ISSUE_CREATE_API_FULFILLED,
  ISSUE_CREATE_API_REJECTED,
  ISSUE_CREATE_START,
  ISSUE_INSTANCE_FETCH_API,
  ISSUE_INSTANCE_FETCH_API_FULFILLED,
  ISSUE_INSTANCE_FETCH_PAYLOAD_INJECT,
  ISSUE_INSTANCE_FETCH_START,
  ISSUE_UPDATE_API,
  ISSUE_UPDATE_API_FULFILLED,
  ISSUE_UPDATE_API_REJECTED,
  ISSUE_UPDATE_START,
  ISSUES_COLLECTION_RESET,
  ISSUES_FETCH_API,
  ISSUES_FETCH_API_FULFILLED,
  ISSUES_FETCH_API_REJECTED,
  ISSUES_FETCH_END,
  ISSUES_FETCH_LOADING_START,
  ISSUES_FETCH_PAYLOAD_INJECT,
  ISSUES_FETCH_SEARCH_API,
  ISSUES_FETCH_SEARCH_START,
  ISSUES_FETCH_START,
  JSONAPI_CONFIGURE,
  LINK_ISSUE_TO_ENTITY,
  OPEN_PUSHPIN_SELECTION,
  openIframe,
  OXYGEN_CONFIGURE_ASYNC,
  OXYGEN_CONFIGURE_START,
  OXYGEN_CONFIGURE_USERS_START,
  OXYGEN_CONFIGURE_USERS_ASYNC,
  sheetChangeAction,
  showNotificationAction,
  trackEvent,
  UNLINK_ISSUE_FROM_ENTITY,
  updatePushpinAction,
  fetchChecklistWithIssues,
  fetchChecklistInstance,
  configureOxygenUserAction
} from '../actions';
import lodashGet from 'lodash/get';
import lodashUniq from 'lodash/uniq';
import {ISSUE_FILTER_PROPERTIES} from '../../constants/issue-query-params';
import {
  addComment,
  assigneeChange,
  deleteAttachment,
  getAssigneeTypeByAssigneeId,
  statusChange
} from '../../utils/analytics-helpers';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import {updateIssueWithSnapshotUrn} from '../../utils/pushpin-helpers';
import {issueUpdatedMessageToParent} from '../../utils/docsIframeMessage-helper';
import {getChecklistUrn} from '../../utils/checklist-helper';

const SHOULD_GENERATE_THUMBNAIL = 'shouldGenerateThumbnail';

// TODO: Move all *_START actions to thunk actions, that returns the promise itself. Then use the promise to operate on success/fail from the components.
export default (jsonApi, issuesV2Api) => store => next => action => {
  const state = store.getState();

  const getIssuesV2 = query =>
    featureFlags.get(FEATURE_FLAGS.USE_ISSUES_BATCH_GET)
      ? issuesV2Api.getIssuesBatchGet(query)
      : issuesV2Api.getIssues(query);
  const getIssues = query =>
    featureFlags.get(FEATURE_FLAGS.ISSUES_V2) ? getIssuesV2(query) : jsonApi.getQualityIssues(query);

  // eslint-disable-next-line default-case
  switch (action.type) {
    case JSONAPI_CONFIGURE:
      jsonApi.setConfig(action.payload);
      issuesV2Api.setConfig(action.payload);
      break;
    case OXYGEN_CONFIGURE_START: {
      const {usersPromise, companiesPromise, rolesPromise} = action.payload;
      store.dispatch({
        type: OXYGEN_CONFIGURE_ASYNC,
        payload: Promise.all([usersPromise, companiesPromise, rolesPromise]).then(([users, companies, roles]) => {
          users.pagination && store.dispatch(configureOxygenUserAction(users));
          return {
            users: users.results || users,
            companies,
            roles
          };
        })
      });
      break;
    }
    case OXYGEN_CONFIGURE_USERS_START: {
      const {userPromises} = action.payload;
      store.dispatch({
        type: OXYGEN_CONFIGURE_USERS_ASYNC,
        payload: Promise.all(userPromises).then(results => ({
          users: [
            ...results
              .map(value => value.results)
              .flat()
          ]
        }))
      });
      break;
    }
    case CURRENT_USER_FETCH_START:
      store.dispatch({
        type: CURRENT_USER_FETCH_API,
        payload: issuesV2Api.getCurrentUserPermissions()
      });
      break;
    case ISSUES_FETCH_SEARCH_START: {
      const search = action.payload;
      let query = getFetchParams(state);
      query = {
        ...query,
        queryParams: {...query.queryParams, ...query.hiddenQueryParams, [ISSUE_FILTER_PROPERTIES.SEARCH]: search},
        limit: 5
      };
      store.dispatch({
        type: ISSUES_FETCH_SEARCH_API,
        meta: {query},
        payload: getIssues(query)
      });
      break;
    }
    case ISSUES_FETCH_START: {
      let query = getFetchParams(state);
      query = {
        ...query,
        queryParams: {...query.queryParams, ...query.hiddenQueryParams}
      };

      query.queryParams[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER] = getAllAssignees(state);

      const queryKey = getQueryKey(state);
      const lastQueryKey = getLastQueryKey(state, TYPE_QUALITY_ISSUES);
      const issues = getIssuesCollectionAttributes(state);
      // TODO - after we merge issue visibility feature, we can just check if we're in project view instead of having the component specify "resetCollection"
      const resetCollection = action.payload.resetCollection;

      if (lastQueryKey !== queryKey || resetCollection) {
        store.dispatch({type: ISSUES_FETCH_LOADING_START, payload: {issueType: TYPE_QUALITY_ISSUES}});
        // Clear collection (and pushpins) when offset in query params is 0.
        // If resetCollection was specified, we clear the collection after we get the data.
        if (query.offset === 0 && !resetCollection) {
          store.dispatch({type: ISSUES_COLLECTION_RESET, payload: {issueType: TYPE_QUALITY_ISSUES}});
          store.dispatch(batchClearPushpinAction(issues));
        }
        store.dispatch({
          type: ISSUES_FETCH_API,
          meta: {query, queryKey, resetCollection},
          payload: getIssues(query)
        });
      }
      break;
    }
    case ISSUES_FETCH_API_FULFILLED: {
      // "meta" from FSA:
      const {
        query,
        query: {offset, limit},
        queryKey,
        resetCollection
      } = action.meta;
      // if the following does not obtain, batch requests are interrupted,
      // and current payload is not injected:
      if (getQueryKeyIsCurrent(state, TYPE_QUALITY_ISSUES, queryKey)) {
        // Reset collection before populating it with new data when a specific offset was requested
        if (resetCollection) {
          const currentlyViewedId = getCurrentPreviewIssueId(state);

          store.dispatch({
            type: ISSUES_COLLECTION_RESET,
            payload: {issueType: TYPE_QUALITY_ISSUES, currentlyViewedId}
          });
        }

        let batchFetchComplete = true;
        // redispatch action and payload for injection:
        store.dispatch({...action, type: ISSUES_FETCH_PAYLOAD_INJECT});

        // Map issues document URN and use DocsSDK to fetch documents by urns
        const documentCollection = getDocumentCollection(state);
        const urns = lodashUniq(action.payload.map(getUrn)).filter(urn => urn && !documentCollection[urn]);

        store.dispatch(fetchDocumentsWithIssues(urns));

        if (isFieldManagementActive(state) && featureFlags.get(FEATURE_FLAGS.CHECKLIST_INDICATOR)) {
          const checklistCollection = getChecklistCollection(state);
          // should get all checklist urns in issues and check if we don't already have these checklist instance in checklists collection
          const checklistUrns = lodashUniq(action.payload.map(getChecklistUrn)).filter(
            urn => urn && !checklistCollection.instances[urn]
          );
          store.dispatch(fetchChecklistWithIssues(checklistUrns));
        }

        // Batch-create pushpins using entire payload:
        store.dispatch(batchCreatePushpinAction(action.payload));
        if (action.payload.length === limit && !resetCollection) {
          // continue with chained, batch requests...
          const firstRecord = action.payload[0];
          // "meta" from IssuesJsonApi: (for determining length of entire issues collection)
          const {
            [ISSUE_ATTRIBUTE_NAMES.PAGINATION]: {totalResults}
          } = firstRecord;
          const newOffset = offset + limit;
          if (newOffset < totalResults) {
            batchFetchComplete = false;
            const newQuery = {...query, ...{offset: newOffset}};
            store.dispatch({
              type: ISSUES_FETCH_API,
              meta: {query: newQuery, queryKey},
              payload: getIssues(newQuery)
            });
          }
        }
        if (batchFetchComplete) {
          store.dispatch({type: ISSUES_FETCH_END, payload: {issueType: TYPE_QUALITY_ISSUES}});
        }
      }
      break;
    }
    case ISSUES_FETCH_API_REJECTED: {
      if (action.meta.resetCollection) {
        const currentlyViewedId = getCurrentPreviewIssueId(state);
        store.dispatch({
          type: ISSUES_COLLECTION_RESET,
          payload: {issueType: TYPE_QUALITY_ISSUES, currentlyViewedId}
        });
      }
      break;
    }
    case ISSUE_INSTANCE_FETCH_START:
      store.dispatch({
        type: ISSUE_INSTANCE_FETCH_API,
        meta: Object.assign({}, action.meta, {id: action.payload}),
        payload: featureFlags.get(FEATURE_FLAGS.ISSUES_V2)
          ? issuesV2Api.getIssue(action.payload)
          : jsonApi.getQualityIssueById(action.payload)
      });
      break;
    case ISSUE_INSTANCE_FETCH_API_FULFILLED: {
      // In case the issue has sheet details and we also have the currently viewed sheet details,
      // check for a match in either sheet guid or sheet name. If both are different, we trigger
      // the SHEET_CHANGE event for the using app to catch and change the currently viewed sheet accordingly.
      const currentSheets = getCurrentSheets(state);
      if (currentSheets.length && !isIssueInSheet(action.payload, currentSheets)) {
        const {
          details: {viewable}
        } = getLinkedDocument(action.payload);
        store.dispatch(
          sheetChangeAction({
            guid: viewable.guid,
            name: viewable.name,
            viewableId: viewable.viewableId
          })
        );
      }

      // redispatch action and payload for injection:
      store.dispatch({...action, type: ISSUE_INSTANCE_FETCH_PAYLOAD_INJECT});

      const urn = getUrn(action.payload);
      if (urn) {
        store.dispatch(fetchDocumentDetailsIfMissing(urn));
      }

      if (isFieldManagementActive(state) && featureFlags.get(FEATURE_FLAGS.CHECKLIST_INDICATOR)) {
        const checklistInstanceUrn = getChecklistUrn(action.payload);
        // refresh checklist details on issue preview
        store.dispatch(fetchChecklistInstance(checklistInstanceUrn));
      }

      break;
    }
    case ISSUE_CREATE_START: {
      const {issuePushpinId} = action.meta;
      const issue = {...action.payload};

      if (issuePushpinId) {
        // If we already saved the relevant snapshot, save the issue with the thumbnail's
        // snapshot URN (instead of PATCHing it after creation)
        const pendingThumbnail = getPendingPushpinThumbnails(state)[issuePushpinId];

        if (pendingThumbnail && pendingThumbnail.snapshotUrn) {
          issue[ISSUE_ATTRIBUTE_NAMES.SNAPSHOT_URN] = pendingThumbnail.snapshotUrn;
          store.dispatch(clearPendingThumbnail(issuePushpinId));
        }
      }
      if (
        featureFlags.get(FEATURE_FLAGS.ISSUES_V2) &&
        featureFlags.get(FEATURE_FLAGS.PASS_SHOULD_GENERATE_THUMBNAIL_TO_ISSUES_V2) &&
        action.payload.linkedDocuments?.length > 0
      ) {
        issue[SHOULD_GENERATE_THUMBNAIL] = false;
      }
      store.dispatch({
        type: ISSUE_CREATE_API,
        meta: action.meta,
        payload: featureFlags.get(FEATURE_FLAGS.ISSUES_V2)
          ? issuesV2Api.createIssue(issue)
          : jsonApi.createQualityIssue(issue)
      });

      // Fetch linked document's details in case we don't have them already (this is possible in project view only)
      const urn = getUrn(issue);
      if (getCurrentViewType(state) === viewTypes.PROJECT && urn) {
        store.dispatch(fetchDocumentDetailsIfMissing(urn));
      }

      break;
    }
    case ISSUE_CREATE_API_FULFILLED: {
      const {issuePushpinId, onSuccess} = action.meta;
      const response = action.payload;
      if (issuePushpinId) {
        // If we already saved the relevant snapshot, update the issue with the thumbnail's snapshot URN
        const pendingThumbnail = getPendingPushpinThumbnails(store.getState())[issuePushpinId];

        if (pendingThumbnail?.snapshotUrn) {
          const issueId = response.id;

          updateIssueWithSnapshotUrn(
            jsonApi,
            issuesV2Api,
            store.dispatch,
            issueId,
            pendingThumbnail.snapshotUrn,
            issuePushpinId
          );
        }
      }

      store.dispatch(batchCreatePushpinAction([response]));
      onSuccess && onSuccess({response});

      break;
    }
    case ISSUE_CREATE_API_REJECTED: {
      store.dispatch(clearUploadingAttachmentsListByOrigin(attachmentOrigin.CREATE_FORM));
      const {onError} = action.meta;
      const error = action.payload && action.payload.response;
      onError && onError(error);
      break;
    }
    case ISSUE_UPDATE_START: {
      const {issue, attributes} = action.payload;
      // Required for analytics events, we calc them here so we won't have to
      // carry the entire `attributes` object to ISSUE_UPDATE_API action
      const isChangingAssignee =
        attributes[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO] &&
        attributes[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO] !== issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO];
      // When unlinking, we sometimes want to update the viewer state
      const isUnlinking =
        attributes[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS] &&
        attributes[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS].length === 0 &&
        issue[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS].length;
      // Some PATCH requests need to explicitly be made to issuesV2, so we allow that here
      const useIssuesV2 = action.meta.forceIssuesV2Api === true || featureFlags.get(FEATURE_FLAGS.ISSUES_V2);
      // To not generate thumbnails from BE
      if (
        useIssuesV2 &&
        featureFlags.get(FEATURE_FLAGS.PASS_SHOULD_GENERATE_THUMBNAIL_TO_ISSUES_V2) &&
        attributes[ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS]?.length > 0
      ) {
        attributes[SHOULD_GENERATE_THUMBNAIL] = false;
      }
      store.dispatch({
        type: ISSUE_UPDATE_API,
        meta: {
          changeToStatus: lodashGet(action.meta, 'changeToStatus'),
          id: issue.id,
          onSuccess: action.meta.onSuccess,
          onError: action.meta.onError,
          isChangingAssignee,
          isUnlinking,
          isAddingComment: action.meta.isAddingComment
        },
        payload: useIssuesV2
          ? issuesV2Api.updateIssue(issue.id, attributes)
          : jsonApi.updateQualityIssue(issue, attributes)
      });
      break;
    }
    case ISSUE_UPDATE_API_FULFILLED: {
      const {onSuccess, changeToStatus} = action.meta;
      const issue = action.payload;
      const currentIssue = getCurrentPreviewIssueAttributes(state);

      if (!!changeToStatus && SHOW_TOAST_STATUS_LIST.includes(changeToStatus)) {
        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.TOAST,
            level: NotificationLevels.SUCCESS,
            message: i18n.t(`edit.transitionToast.${changeToStatus}.success`, {
              identifier: issue[ISSUE_ATTRIBUTE_NAMES.ID],
              issueType: issue.type
            })
          })
        );
      }

      const {isChangingAssignee, isUnlinking, isAddingComment} = action.meta;
      const issueTypesAndSubTypesTitleMap = getFieldIssueTypesAndSubTypesIdToTitleMap(state);
      const issueTypeTitle = issueTypesAndSubTypesTitleMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]];
      const issueSubTypeTitle = issueTypesAndSubTypesTitleMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]];

      if (changeToStatus) {
        // Track statusChange analytics event
        store.dispatch(
          trackEvent(
            statusChange(
              currentIssue.status,
              changeToStatus,
              issueTypeTitle,
              issueSubTypeTitle,
              isChangingAssignee,
              isAddingComment
            )
          )
        );
      } else if (isChangingAssignee) {
        // Track assigneeChange analytics event
        const currentUserId = getCurrentUserId(state);
        const prevAssignee = getAssigneeTypeByAssigneeId(
          currentIssue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO],
          currentUserId,
          getOxygenMappings(state),
          currentIssue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]
        );
        const newAssignee = getAssigneeTypeByAssigneeId(
          issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO],
          currentUserId,
          getOxygenMappings(state),
          issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]
        );
        store.dispatch(trackEvent(assigneeChange(issueTypeTitle, issueSubTypeTitle, prevAssignee, newAssignee)));
      } else if (isUnlinking) {
        // If we're still in document view, re-fetch issues and delete the unlinked issue's pushpin
        const viewType = getCurrentViewType(state);
        if (viewType === viewTypes.DOCUMENT) {
          store.dispatch({type: ISSUES_COLLECTION_RESET, payload: {issueType: TYPE_QUALITY_ISSUES}});
          store.dispatch(fetchIssuesAction({resetCollection: true}));
          store.dispatch(clearPushpinAction(currentIssue));
        }
      }

      // Update issue pushpin
      if (hasLinkedDocument(issue)) {
        const {id, [ISSUE_ATTRIBUTE_NAMES.ID]: label, [ISSUE_ATTRIBUTE_NAMES.STATUS]: status} = issue;
        const {
          details: {position, objectId, viewerState, externalId, attributesVersion}
        } = getLinkedDocument(issue);
        store.dispatch(
          updatePushpinAction({
            id,
            label,
            status,
            position,
            objectId,
            viewerState,
            externalId,
            attributesVersion
          })
        );
      }

      // Shows notification when user changes subtype, and the change resulted in different custom attributes
      // which already hold value
      const updatedIssueCustomAttributes = issue[ISSUE_ATTRIBUTE_NAMES.CUSTOM_ATTRIBUTES] || [];
      const currentIssueCustomAttributesIds = updatedIssueCustomAttributes.map(
        customAttribute => customAttribute.attributeDefinitionId
      );
      const hasRestoredChanges = updatedIssueCustomAttributes
        .filter(customAttribute => !currentIssueCustomAttributesIds.includes(customAttribute.attributeDefinitionId))
        .some(customAttribute => !isNullOrUndefined(customAttribute.value));
      if (hasRestoredChanges) {
        const issueSubType = getIssueTypeById(state, issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]);
        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.TOAST,
            level: NotificationLevels.SUCCESS,
            message: i18n.t('edit.customAttributesRestored', {
              issueTypeName: issueSubType.title
            })
          })
        );
      }

      // Success callback
      onSuccess && onSuccess(issue);

      //update message to parent window in case document is opened is iframe
      issueUpdatedMessageToParent(getCurrentViewType(state), issue.id);
      break;
    }
    case ISSUE_UPDATE_API_REJECTED: {
      const {onError, changeToStatus} = action.meta;

      onError && onError();

      if (!!changeToStatus && SHOW_TOAST_STATUS_LIST.includes(changeToStatus)) {
        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: i18n.t(`edit.transitionToast.${changeToStatus}.fail`, {issueType: TYPE_QUALITY_ISSUES})
          })
        );
      } else {
        store.dispatch(
          showNotificationAction({
            type: NotificationTypes.SNACKBAR,
            level: NotificationLevels.ERROR,
            message: i18n.t('edit.error.general')
          })
        );
      }

      break;
    }
    case COMMENT_CREATE_START: {
      store.dispatch({
        type: COMMENT_CREATE_API,
        meta: {id: action.meta.id, resolve: action.meta.resolve, reject: action.meta.reject},
        payload: issuesV2Api.createComment(action.meta.id, action.payload)
      });
      break;
    }
    case COMMENT_CREATE_API_FULFILLED: {
      if (action.meta.resolve) {
        action.meta.resolve();
      }

      const issue = getIssueAttributesById(state, action.payload.issueId);
      const issueTypesAndSubTypesTitleMap = getFieldIssueTypesAndSubTypesIdToTitleMap(state);
      const issueTypeTitle = issueTypesAndSubTypesTitleMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_TYPE_ID]];
      const issueSubTypeTitle = issueTypesAndSubTypesTitleMap[issue[ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID]];

      // TODO - this is "hacky" we should figure out a different way to figure out if the comment contains a mention
      const isMention = action.payload.body.includes('@{"type":');
      store.dispatch(trackEvent(addComment(issueTypeTitle, issueSubTypeTitle, isMention)));
      break;
    }
    case COMMENT_CREATE_API_REJECTED: {
      if (action.meta.reject) {
        action.meta.reject();
      }
      break;
    }
    case ATTACHMENT_UPLOAD_FAILED: {
      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.SNACKBAR,
          level: NotificationLevels.ERROR,
          message: i18n.t('errors.attachmentUploadError')
        })
      );
      break;
    }
    case FIELD_ISSUES_ACTIVITY_FETCH_ATTACHMENTS:
    case ATTACHMENTS_FETCH_API_FULFILLED: {
      store.dispatch(fetchAttachmentThumbnailsAction(action.payload));
      break;
    }
    case ATTACHMENT_DELETE_API_FULFILLED: {
      // track attachment deletion event
      const {isOwner, status, assignedTo, assignedToType} = action.meta.analyticsObj;

      const oxygen = getOxygenMappings(state);
      const currentUserId = getCurrentUserId(state);
      const assignee = getAssigneeTypeByAssigneeId(assignedTo, currentUserId, oxygen, assignedToType);
      const viewType = getCurrentViewType(state);
      store.dispatch(trackEvent(deleteAttachment(viewType, assignee, isOwner, status)));

      break;
    }
    case ATTACHMENT_DELETE_API_REJECTED: {
      store.dispatch(
        showNotificationAction({
          type: NotificationTypes.SNACKBAR,
          level: NotificationLevels.ERROR,
          message: i18n.t('errors.attachmentDeleteError')
        })
      );
      break;
    }
    case OPEN_PUSHPIN_SELECTION: {
      const projectId = getProjectId(state);
      const document = action.payload.document;
      const requestDomainUrl = window.location.origin;

      const baseUrl = `${ENV_CONFIGS.DOCS}/projects/${projectId}`;

      const urlQueryParams = `?droppin=${TYPE_QUALITY_ISSUES}&pinstatus=${
        action.payload.status
      }&requestDomain=${requestDomainUrl}`;
      const url = `${baseUrl}/viewer/items/${document.urn}${urlQueryParams}`;
      const title = lodashGet(document, 'currentVersion.name');

      // `openIframe` is here for `IssueCreateModal`. In the project view flow we open the iframe using the above `changeLocationAction` action.
      store.dispatch(openIframe(url, title));
      break;
    }
    case CLOSE_PUSHPIN_SELECTION: {
      store.dispatch(closeIframe());
      break;
    }
    case LINK_ISSUE_TO_ENTITY: {
      issuesV2Api
        .linkIssueToEntity(action.meta.id, action.payload.addedLinks)
        .then(data => action.meta.resolve(data.links), action.meta.reject);
      break;
    }
    case UNLINK_ISSUE_FROM_ENTITY: {
      issuesV2Api
        .unlinkIssueFromEntity(action.meta.id, action.payload.removedLink)
        .then(data => action.meta.resolve(data.links), action.meta.reject);
      break;
    }
  }

  // eslint-disable-next-line consistent-return
  return next(action);
};
